/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  ByTransactionInitialProperties,
  ByTransactionResponseInterface,
  RequestByTransactionInterFace,
} from '../../interface/by-transaction-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchByTransactionAccount = createAsyncThunk<
  ByTransactionResponseInterface,
  RequestByTransactionInterFace
>(
  'by-transaction/fetchByTransactionAccount',
  async (data: RequestByTransactionInterFace, { dispatch }) => {
    try {
      const response = await accountApi.fetchRevenueByTransactionList(removeEmptyKeys({ ...data }));
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// Thunk definition
export const fetchByTransactionAccountFilters = createAsyncThunk<
  CommonFilterProperties[],
  { accountCustomId: string } // payload type
>('by-transaction/fetchByTransactionAccountFilters', async ({ accountCustomId }, { dispatch }) => {
  if (!accountCustomId) {
    throw new Error('accountCustomId is required');
  }

  try {
    const response = await accountApi.fetchByTransactionListFilters({ accountCustomId });
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationMessage',
      payload: {
        type: 'error',
        message: error?.response?.data?.message || 'Something went wrong!',
        code: error?.response?.data?.code || 200,
        status: true,
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ByTransactionInitialProperties = {
  ByTransaction: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const byTransactionSlice = createSlice({
  name: 'by-transaction',
  initialState,
  reducers: {
    /**
     * Update By Transaction filters
     * @param state
     * @param action
     */
    updateByTransactionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Clear all Data and reset related state
     */
    clearByTransactionData: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableByTransactionFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<ByTransactionInitialProperties>) => {
    builder
      .addCase(fetchByTransactionAccount.pending, (state: ByTransactionInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchByTransactionAccount.fulfilled,
        (
          state: ByTransactionInitialProperties,
          action: PayloadAction<ByTransactionResponseInterface>,
        ) => {
          state.loading = false;
          state.ByTransaction = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchByTransactionAccount.rejected, (state: ByTransactionInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchByTransactionAccountFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchByTransactionAccountFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter((filterValue) => filterValue.key !== 'startDate_endDate');
        },
      )
      .addCase(fetchByTransactionAccountFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const {
  updateByTransactionFilters,
  disableByTransactionFilterApplied,
  setSortData,
  clearByTransactionData,
} = byTransactionSlice.actions;
// Export the reducer
export default byTransactionSlice.reducer;
